import { FC, useMemo, useCallback } from 'react'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { meetingsSelector, nextScheduledMeetingSelector } from 'components/features/coaching/advisorMeeting/selectors/advisorsSelector'
import Spinner from 'components/features/pfm/Spinner/Spinner'
import { MeetingDetails } from 'components/features/coaching/advisorPage/MeetingDetails/MeetingDetails'
import Button from 'components/features/pfm/Button/Button'
import styles from 'components/features/coaching/advisorPage/PageContent/PageContent.styl'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { hadAppointedMeetingSelector } from 'selectors/globalSelectors'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { COACH_REVIEW_PAGE } from 'constants/pages'
import moment from 'moment'
import RadialProgressbar from 'components/features/pfm/RewardSummary/RadialProgressbar'
import { TwoColumnRowLayout } from 'components/layouts/Layouts'
import HowItWorks from 'components/features/coaching/advisorPage/HowItWorks/HowItWorks'
import ModuleWrapper2020 from 'components/features/pfm/ModuleWrapper2020/ModuleWrapper2020'
import { ElligibilityCountdown } from 'components/features/coaching/advisorPage/ElligibilityCountdown/ElligibilityCountdown'


export const PageContent: FC = () => {
  const dispatch = useStoreDispatch()
  const meetings = useStoreSelector(meetingsSelector)
  const nextScheduledMeeting = useStoreSelector(nextScheduledMeetingSelector)
  const hadAppointedMeeting = useStoreSelector(hadAppointedMeetingSelector)

  const mainChild = useMemo(() => {
    if (!meetings) {
      return (
        <div className={ styles.spinnerWrapper }><Spinner size='large' /></div>
      )
    }

    if (nextScheduledMeeting) {
      const { uid, advisorUid, status, timeslot } = nextScheduledMeeting
      return (
        <>
          <MeetingDetails
            advisorUid={ advisorUid }
            status={ status }
            timeslot={ timeslot }
          />
          <Button
            text='See Details'
            fullWidth
            qaMarker='see-details'
            onClick={ () => {
              dispatch(modifyFloatingPaneActionCreator('advisor', { mode: 'meeting', uid }))
            } }
          />
        </>
      )
    }

    if (hadAppointedMeeting) {
      const prevMeetingUid = meetings?.allKeys.find((uid) => {
        const meeting = meetings.byKey[uid]
        return meeting.status === 'completed' && !meeting.is_reviewed
      })

      const reviewReminder = !!prevMeetingUid && (
        <>
          <p>In the meantime, rate your last session. It helps us find the best coaches for you.</p>
          <NavigationLink to={ COACH_REVIEW_PAGE } search={ `?meeting_uid=${ prevMeetingUid }` } >
            <Button text='Rate Your Session' fullWidth />
          </NavigationLink>
        </>
      )

      const dayNoOfMonth = moment().date()
      const firstDayNextMonth = moment().endOf('month').date() + 1
      return (
        <div className={ styles.countdownWrapper }>
          <p>Schedule your next session in</p>
          <ElligibilityCountdown value={ firstDayNextMonth - dayNoOfMonth } isEmphasized>
            <RadialProgressbar value={ dayNoOfMonth / firstDayNextMonth } theme='blue' />
          </ElligibilityCountdown>
          { reviewReminder }
        </div>
      )
    }

    return null
  }, [dispatch, meetings, nextScheduledMeeting, hadAppointedMeeting])

  const handleScheduleSession = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator('advisor', {
      mode: 'request',
      step: 'topic',
      fields: {
        flow: 'regular',
      },
    }))
  }, [dispatch])


  if (mainChild) {
    return (
      <TwoColumnRowLayout
        left={ (
          <ModuleWrapper2020
            title='Next Session'
            shadowTheme='medium'
            forceRoundedCorners
            isFlexLayout
            className={ styles.nextSession }
          >
            { mainChild }
          </ModuleWrapper2020>
        ) }
        right={ (
          <ModuleWrapper2020
            title='How It Works'
            shadowTheme='medium'
            forceRoundedCorners
            isFlexLayout
            className={ styles.howItWorks }
          >
            <HowItWorks />
          </ModuleWrapper2020>
        ) }
      />
    )
  }

  return (
    <ModuleWrapper2020
      title='How It Works'
      shadowTheme='medium'
      forceRoundedCorners
      isFlexLayout
      className={ styles.howItWorks }
    >
      <div className={ styles.scheduleNewSession }>
        <HowItWorks />
        <div className={ styles.scheduleNewSessionRight }>
          <div className={ styles.image } data-qa-marker='image' />
          <Button fullWidth onClick={ handleScheduleSession } qaMarker='schedule-session-button'>SCHEDULE YOUR SESSION</Button>
        </div>
      </div>
    </ModuleWrapper2020>
  )
}
